// src/pages/outsourcing_management/InquiryDialog.tsx
//
// The inquiry dialog - the only one. Raising an inquiry, reading one and
// editing one all happen here, because they are the same record and used to be
// three differently-shaped views of it: a create modal
// (components/Inquiry/InquiryModal wrapping InquiryComponent), an inline panel
// that expanded under the table row, and this dialog, whose Edit button closed
// itself and reopened the create modal in a mode of its own.
//
// Which half it shows is decided by `inquiry`: pass one to read it, omit it to
// raise a new one. `editable` turns on the Edit button, which swaps the facts
// grid and the read-only item lines for InquiryEditForm in place - the two
// columns beside them stay where they are, so the inquiry never leaves the
// screen to be edited.
//
// Built to the same plan as RequisitionDetailDialog, in three columns: the
// inquiry itself on the left with its item lines under it, the supplier email
// beside it, and the actions and linked records on the right. The two
// Outsourcing tabs previously disagreed about what a table row is - a
// requisition row opened a dialog, an inquiry row expanded an inline panel and
// carried a status dropdown of its own. They behave the same way now. Both
// halves render through DialogColumns, so nothing moves when a new inquiry
// becomes a saved one.
//
// There is no History panel. The requisition timeline is fed by
// REQUISITION_AUDIT; inquiries have no equivalent table, only the generic
// EVENTLOGS rows BaseAuditableModel writes, which nothing exposes over the
// API. Rather than draw a panel with no data behind it, the space goes to the
// email preview - the one thing here that shows what the supplier actually
// receives.
import {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import { Download, OpenInFull } from "@mui/icons-material";
import AppStateContext from "contexts/AppStateContext";
import { CartType } from "types/CartTypes";
import { OrderType } from "types/OrderTypes";
import {
  DeliveryProgressType,
  InquiryDetailType,
  InquiryType,
  hasLines,
} from "types/InquiryTypes";
import {
  apiConfirmOrder,
  apiConvertToPurchaseOrder,
  apiGetInquiry,
  apiPreviewInquiryFromId,
} from "util/network/Inquiries";
import {
  StatusAck,
  useAcknowledgeInquiryStatus,
} from "hooks/useInquiryStatusUpdates";
import StatusToken from "components/Dashboard/StatusToken";
import DetailDialogShell from "components/Dashboard/DetailDialogShell";
import {
  inquiryStatusLabel,
  isInquiryFinished,
  isInquiryOrdered,
  isInquiryInDelivery,
  PURCHASE_ORDER_SUBMIT_LABELS,
  INQUIRY_STATUS_COLORS,
  GOODS_IN_COLORS,
} from "./constants/inquiryStatusColors";
import { EmailFullSizeDialog } from "components/Inquiry/EmailPreviewPanel";
import InquiryAuditTrail from "./InquiryAuditTrail";
import InquiryPartnerShortlist from "./InquiryPartnerShortlist";
import DraftEmailPreview, {
  InquiryDraft,
} from "components/Inquiry/DraftEmailPreview";
import InquiryItemsTable from "components/Inquiry/items/InquiryItemsTable";
import {
  isDeliveryInquiry,
  receivedLabel,
  useGoodsIn,
} from "components/Inquiry/items/goodsIn";
import { lineFromInquiryItem } from "components/Inquiry/items/inquiryLine";
import InquiryWorkflowActions from "./InquiryWorkflowActions";
import InquiryCreateForm from "./InquiryCreateForm";
import InquiryEditForm from "./InquiryEditForm";
import InquiryConvertForm, {
  ConvertValues,
} from "./InquiryConvertForm";
import InquiryConvertPreview from "./InquiryConvertPreview";
import { DetailsDisclosure } from "./InquiryDetailsDisclosure";
import { useToast } from "contexts/ToastContext";
import DialogColumns, { ItemLinesScroll, RailScroll } from "./InquiryDialogLayout";
import InquirySourceLines from "./InquirySourceLines";
import EnquiryPurchaseQuantities from "./EnquiryPurchaseQuantities";
import { requisitionStatusLabel } from "./constants/requisitionStatusColors";

type PropsType = {
  open: boolean;
  onClose: () => void;
  /**
   * The inquiry to show. Omit it and the dialog raises a new one instead -
   * there is no third state, because every caller either has an inquiry in
   * hand or is starting one.
   */
  inquiry?: InquiryType;
  /** What a newly raised inquiry attaches to. Ignored when `inquiry` is set. */
  prodObjIn?: OrderType | CartType;
  prodType?: "order" | "cart";
  /**
   * Whether this inquiry may be edited here. Off by default: the order page
   * shows the inquiries raised against an order, and changing one belongs on
   * the sourcing board.
   */
  editable?: boolean;
  /**
   * A save landed. The dialog re-reads the inquiry itself, so this carries
   * nothing - it is only there to tell the list behind it to catch up.
   */
  onSaved?: () => void;
  /** A workflow action landed and the list behind the dialog is now stale. */
  onChanged?: () => void;
  /**
   * Opening the inquiry acknowledged its status marker, or the request for
   * that failed and the marker is back. Only the one row changed, so the
   * list behind patches it rather than refetching (see OutsourcingTable).
   */
  onStatusAck?: (ack: StatusAck) => void;
  /** A new inquiry was raised. */
  onCreated?: (id: number) => void;
};

const InquiryDialog: FunctionComponent<PropsType> = ({
  open,
  onClose,
  inquiry,
  prodObjIn,
  prodType = "order",
  editable = false,
  onSaved,
  onChanged,
  onStatusAck,
  onCreated,
}) => {
  // The inquiry as this dialog currently knows it, re-read after every write
  // so `editable` works on its own without the caller feeding a fresh record
  // back. It is a read rather than the answer to the write because
  // PUT /inquiries/<id> echoes the request body - which carries no id, no
  // partner and no items - and item editing needs the saved lines back with
  // their products on them.
  const { token } = useContext(AppStateContext);
  const [current, setCurrent] = useState(inquiry);
  // Seed from the prop so the dialog paints immediately, then fetch the
  // detail record. Both callers hand us a *list* row, and the list response
  // omits auditTrail and partnerCandidates (an N+1 otherwise, see InquiryVM)
  // - so without this the History panel and the shortlist render their empty
  // states no matter what is actually recorded against the inquiry.
  useEffect(() => {
    setCurrent(inquiry);
    if (!inquiry) return;
    let cancelled = false;
    apiGetInquiry(token, inquiry.id)
      .then((detail) => {
        if (!cancelled) setCurrent(detail);
      })
      // The list row is still worth showing; only the detail-only panels
      // stay empty.
      .catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [inquiry, token]);

  const reload = () => {
    if (inquiry === undefined) return;
    apiGetInquiry(token, inquiry.id).then(setCurrent).catch(console.error);
  };

  // Opening the inquiry is the acknowledgement of whatever reached it from
  // the requisition side - no click on the marker itself, same as
  // RequisitionDetailDialog. Fires once the loaded record actually has
  // something pending, so a reopen of an acknowledged one is not a request.
  //
  //
  // Optimistic: the board's row loses its marker as the request goes out,
  // and the hook guesses the menu badge down; a failure puts both back.
  // Nothing is refetched for it - the board used to be, and every open
  // flashed the whole table to skeletons for one flag on one row. `current`
  // keeps its flag: the dialog does not show it, and clearing it would let
  // the detail read above, landing after, set it again and fire a second
  // request.
  const { mutate: acknowledgeStatus } = useAcknowledgeInquiryStatus(token, {
    onMutate: (id) => onStatusAck?.({ id, pending: false }),
    onError: (_error, id) => onStatusAck?.({ id, pending: true }),
  });
  const pendingAckId =
    open && current?.statusAckPending ? current.id : undefined;
  useEffect(() => {
    if (pendingAckId !== undefined) acknowledgeStatus(pendingAckId);
  }, [pendingAckId, acknowledgeStatus]);

  // Raising the inquiry is the whole job here. It used to offer to hang the
  // new inquiry on a production task next, but that picker listed every task
  // with allowInquiries and preselected the first one, so a CNC inquiry was
  // offered - and defaulted to - the MATERIAL task. Linking now happens from
  // the production board, where the task is in front of you
  // (CustomTaskButton -> allowInquiries).
  const handleCreated = (id: number) => {
    onCreated?.(id);
    onClose();
  };

  return (
    <>
      {/* Same shell as RequisitionDetailDialog - the item tables live in the
          left of three columns now, and they need the room. */}
      <DetailDialogShell
        open={open}
        onClose={onClose}
        title={
          current
            ? // Once the purchase order has gone out the dialog is about that
              // order, and PO #<id> is the number the supplier was sent.
              isInquiryOrdered(current)
              ? `PO #${current.id}`
              : current.inquiryNo ?? `Inquiry #${current.id}`
            : "New inquiry"
        }
      >
        {current ? (
          <InquiryDetailContent
            inquiry={current}
            editable={editable}
            onSaved={() => {
              reload();
              onSaved?.();
            }}
            onChanged={() => {
              // The dialog holds its own copy, so a workflow action has to be
              // read back here as well as behind: sending moves the inquiry to
              // Sent / Awaiting Response, and the status chip and the Send
              // button both describe what the copy says.
              reload();
              onChanged?.();
            }}
            onNavigate={onClose}
            onReplace={setCurrent}
          />
        ) : (
          // Remounted per opening, so a cancelled inquiry does not come back
          // half-filled the next time the button is pressed.
          open && (
            <InquiryCreateForm
              prodObjIn={prodObjIn}
              prodType={prodType}
              onCancel={onClose}
              onCreated={handleCreated}
            />
          )
        )}
      </DetailDialogShell>
    </>
  );
};

type ContentPropsType = {
  inquiry: InquiryType;
  editable: boolean;
  onSaved: () => void;
  onChanged: () => void;
  /** Navigating away unmounts the page under the dialog, so close it first. */
  onNavigate: () => void;
  /** Swap the dialog's copy - an optimistic patch, or a write's answer. */
  onReplace: (next: InquiryDetailType) => void;
};

const InquiryDetailContent: FunctionComponent<ContentPropsType> = ({
  inquiry,
  editable,
  onSaved,
  onChanged,
  onNavigate,
  onReplace,
}) => {
  // A closed inquiry is a record of what happened, not a working document.
  // Done as well as Closed: neither can be edited any more.
  const isClosed = isInquiryFinished(inquiry.state);
  // Before Ordered the shortlist is shown and the final supplier is not;
  // from Ordered on it is the other way round - see isInquiryOrdered.
  const ordered = isInquiryOrdered(inquiry);
  // From Pickup / Delivery on, goods are arriving and counting them per line
  // is the work; the item lines grow the counts and the email gives up its
  // column to them - the dialog is two columns and the lines take the room.
  const inDelivery = isInquiryInDelivery(inquiry.state);
  const goodsIn = useGoodsIn(inquiry, onChanged);
  // The dialog opens on the board's row, which has no lines - those come
  // with the detail fetch. Everything that reads or writes lines waits for it.
  const detail = hasLines(inquiry) ? inquiry : undefined;
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const [editing, setEditing] = useState(false);
  // What the edit form currently holds. Undefined until it reports, and left
  // behind when editing stops - the saved preview takes over there anyway.
  const [draft, setDraft] = useState<InquiryDraft | undefined>(undefined);
  // Both halves of placing an order happen here, in the dialog - same as
  // editing, not a second dialog on top of this one. The mode swaps the left
  // column for the order form, the email column for the purchase-order
  // preview (only when something is about to be emailed), and the actions
  // for a single Confirm / Cancel.
  const [orderMode, setOrderMode] = useState<"convert" | "confirm" | null>(
    null
  );
  const converting = orderMode !== null;
  const [convertDraft, setConvertDraft] = useState<ConvertValues>({
    deadline: "",
    deliveryMethod: "",
    message: "",
    leftOut: [],
  });
  const [convertingBusy, setConvertingBusy] = useState(false);

  const startConvert = () => {
    // The form seeds its own values from the inquiry and reports them back;
    // all this does is turn the mode on.
    setOrderMode("convert");
  };
  const startConfirmOrder = () => {
    setOrderMode("confirm");
  };
  const cancelConvert = () => {
    setOrderMode(null);
    setConvertDraft({ deadline: "", deliveryMethod: "", message: "", leftOut: [] });
  };
  // Recording the supplier's answer. Nothing is required - an order confirmed
  // exactly as it was sent is confirmed by pressing the button - and the
  // supplier cannot change, so it is not sent.
  const confirmOrder = () => {
    setConvertingBusy(true);
    apiConfirmOrder(token, inquiry.id, {
      deadline: convertDraft.deadline || undefined,
      deliveryMethod: convertDraft.deliveryMethod || undefined,
      itemPrices: convertDraft.itemPrices,
      transportCostCents: convertDraft.transportCostCents,
      deliveryTime: convertDraft.deliveryTime?.trim() || undefined,
      message: convertDraft.message || undefined,
    })
      .then(() => {
        setOrderMode(null);
        setConvertingBusy(false);
        addToast({
          type: "success",
          message: `Inquiry #${inquiry.id} ordered - the supplier confirmed`,
        });
        onChanged();
      })
      .catch((err) => {
        setConvertingBusy(false);
        addToast({
          type: "error",
          message: `Inquiry #${inquiry.id} was not confirmed: ${err}`,
          keep: true,
        });
      });
  };
  const confirmConvert = () => {
    if (
      !convertDraft.partnerId ||
      !convertDraft.deadline ||
      !convertDraft.deliveryMethod ||
      convertDraft.pricesValid === false
    )
      return;
    setConvertingBusy(true);
    apiConvertToPurchaseOrder(token, inquiry.id, {
      partnerId: convertDraft.partnerId,
      deadline: convertDraft.deadline,
      deliveryMethod: convertDraft.deliveryMethod,
      itemPrices: convertDraft.itemPrices,
      transportCostCents: convertDraft.transportCostCents,
      message: convertDraft.message,
      excludedFiles: convertDraft.leftOut.length
        ? convertDraft.leftOut
        : undefined,
    })
      .then(() => {
        setOrderMode(null);
        setConvertingBusy(false);
        addToast({
          type: "success",
          message: `Inquiry #${inquiry.id} - purchase order sent`,
        });
        // The linked requisition followed (status and planned delivery), and
        // it is on screen elsewhere - same as any workflow move.
        onChanged();
      })
      .catch((err) => {
        setConvertingBusy(false);
        // A failed send leaves the inquiry where it was, server-side, so the
        // convert view stays open with everything filled in for another go.
        addToast({
          type: "error",
          message: `Inquiry #${inquiry.id} - the purchase order was not sent: ${err}`,
          keep: true,
        });
      });
  };
  // The history is filtered to what matters in the state you are in. In
  // Pickup / Delivery the arriving goods are the story, so the trail opens
  // on the goods-in rows; the toggle widens it to everything.
  const [historyAll, setHistoryAll] = useState(false);

  const header = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        marginBottom: "0.75rem",
      }}
    >
      <StatusToken
        label={inquiryStatusLabel(inquiry.state)}
        colors={INQUIRY_STATUS_COLORS[inquiry.state]}
      />
      {/* The title switches to the PO number once the order is out; the INQ
          number stays here so it still matches the requisition's links. */}
      {ordered && inquiry.inquiryNo && (
        <Typography variant="body2" color="text.secondary">
          {inquiry.inquiryNo}
        </Typography>
      )}
      {editable && !isClosed && !editing && !converting && (
        <Button
          size="small"
          startIcon={<EditIcon />}
          disabled={!detail}
          onClick={() => setEditing(true)}
        >
          Edit inquiry
        </Button>
      )}
      {converting && (
        <Typography variant="body2" color="text.secondary">
          {orderMode === "confirm"
            ? "Recording the supplier's confirmation"
            : "Converting to a purchase order"}
        </Typography>
      )}
    </div>
  );

  return (
    <DialogColumns
      left={
        <>
          {header}
          {orderMode && detail ? (
            // The order form: only what this half of the order decides is
            // editable - the supplier, date and note on a conversion, the
            // figures the supplier came back with on a confirmation. The rest
            // of the inquiry keeps its normal read view either way.
            <InquiryConvertForm
              inquiry={detail}
              mode={orderMode}
              onValuesChange={setConvertDraft}
            />
          ) : editing && detail ? (
            // The item lines go with it: on a Draft they are editable, and
            // InquiryEditForm owns them so one Save writes the fields and the
            // lines together.
            <InquiryEditForm
              inquiry={detail}
              onSaved={() => {
                setEditing(false);
                onSaved();
              }}
              onCancel={() => setEditing(false)}
              onDraftChange={setDraft}
              onChanged={onChanged}
            />
          ) : (
            <>
              {/* Before Ordered the shortlist is who is being asked - a
                  working thing, so it stays out in the open rather than
                  hiding in Details. From Ordered on the winner takes its
                  place on the summary strip. */}
              {!ordered && (
                <Box sx={{ mb: 1.5 }}>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ mb: 1 }}
                  >
                    Potential suppliers
                  </Typography>
                  <InquiryPartnerShortlist
                    candidates={inquiry.partnerCandidates ?? []}
                    editable={false}
                  />
                  <Divider sx={{ mt: 1.5 }} />
                </Box>
              )}

              {/* The inquiry facts, always shown open in every state - the
                  grid is there so a buyer can confirm what the inquiry says
                  before acting on it, and folding it away would hide the very
                  thing they are checking. */}
              <DetailsDisclosure inquiry={inquiry} />

              <Divider sx={{ my: 2 }} />

              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ mb: 1 }}
              >
                Item lines
              </Typography>
              {inDelivery && (
                <DeliveryHeadline
                  progress={inquiry.deliveryProgress ?? null}
                  method={inquiry.deliveryMethod}
                  pickup={!isDeliveryInquiry(inquiry)}
                />
              )}
              {!detail ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={14} />
                  <Typography variant="body2" color="text.secondary">
                    Loading lines…
                  </Typography>
                </Box>
              ) : detail.items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No lines on this inquiry.
                </Typography>
              ) : (
                <ItemLinesScroll>
                  <InquiryItemsTable
                    mode="read"
                    lines={detail.items.map(lineFromInquiryItem)}
                    inquiryType={inquiry.type}
                    goodsIn={inDelivery ? goodsIn : undefined}
                  />
                </ItemLinesScroll>
              )}
              {/* EE-253 P3: lengths of stock / finishing quantities. */}
              {detail && !editing && (
                <EnquiryPurchaseQuantities inquiry={detail} onReplace={onReplace} />
              )}
              {/* EE-253 P2b: the requisition lines behind them. */}
              {detail && !editing && (
                <InquirySourceLines inquiry={detail} onChanged={onChanged} onNavigate={onNavigate} />
              )}
              {/* One Save for the whole goods-in table: the counts stay in
                  the table until then, so filling a few lines in does not
                  move the status mid-way. */}
              {detail && inDelivery && !goodsIn.readOnly && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mt: 1, justifyContent: "flex-end", alignItems: "center" }}
                >
                  {goodsIn.dirty && !goodsIn.saving && (
                    <Typography variant="caption" color="text.secondary">
                      Unsaved changes
                    </Typography>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    disabled={!goodsIn.dirty || goodsIn.saving}
                    onClick={goodsIn.cancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!goodsIn.dirty || goodsIn.saving}
                    startIcon={
                      goodsIn.saving ? <CircularProgress size={14} /> : undefined
                    }
                    onClick={goodsIn.save}
                  >
                    Save
                  </Button>
                </Stack>
              )}
            </>
          )}
        </>
      }
      // The email is never inline in the read view: it is a document to open
      // when you need it, not a column that crowds the item lines in every
      // state. The one exception is while the edit form is open, where the
      // draft preview must sit beside the fields producing it.
      middleLabel={
        orderMode === "convert" ? "Purchase order preview" : "Email preview"
      }
      middle={
        // Nothing is emailed by a confirmation, so there is nothing to
        // preview beside it - the purchase order it confirms is the one
        // already sent, and the email button opens that.
        orderMode === "convert" ? (
          // The purchase order this conversion would send, beside the form
          // producing it - the one exception to "never inline", exactly like
          // the draft preview beside the edit form.
          <InquiryConvertPreview
            inquiryId={inquiry.id}
            partnerId={convertDraft.partnerId}
            deadline={convertDraft.deadline}
            deliveryMethod={convertDraft.deliveryMethod}
            message={convertDraft.message}
            itemPrices={convertDraft.itemPrices}
            transportCostCents={convertDraft.transportCostCents}
          />
        ) : editing && draft ? (
          <>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Email preview
            </Typography>
            <DraftEmailPreview draft={draft} inquiryId={inquiry.id} />
          </>
        ) : (
          undefined
        )
      }
      right={
        <>
          {/* Everything above the history keeps its height; the history takes
              the rest of the column and scrolls. */}
          <Box sx={{ flexShrink: 0 }}>
            {converting ? (
              <>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 1 }}
                >
                  Actions
                </Typography>
                {/* The workflow buttons give way to a single Confirm / Cancel
                    while the conversion is open - this is the decision the
                    whole convert view is about. */}
                <Stack direction="column" spacing={1} sx={{ alignItems: "stretch" }}>
                  {/* The convert is the decision the whole view is about, so
                      it leads the column like the primary action does in the
                      normal workflow actions; Cancel trails it. */}
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    size="small"
                    disabled={
                      convertingBusy ||
                      // A price that is not an amount would 400 either way.
                      convertDraft.pricesValid === false ||
                      // A confirmation has nothing to fill in: it records what
                      // the supplier said, and "exactly as sent" is an answer.
                      (orderMode === "convert" &&
                        (!convertDraft.partnerId ||
                          !convertDraft.deadline ||
                          !convertDraft.deliveryMethod))
                    }
                    startIcon={
                      convertingBusy ? (
                        <CircularProgress size={14} />
                      ) : undefined
                    }
                    onClick={
                      orderMode === "confirm" ? confirmOrder : confirmConvert
                    }
                  >
                    {PURCHASE_ORDER_SUBMIT_LABELS[orderMode]}
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={cancelConvert}
                    disabled={convertingBusy}
                  >
                    Cancel
                  </Button>
                </Stack>
              </>
            ) : (
              !isClosed && (
                <>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ mb: 1 }}
                  >
                    Actions
                  </Typography>
                  {/* Off while the form is open. The buttons act on the saved
                      inquiry, the form holds an unsaved one, and a send or a
                      move from here mid-edit would go out on whichever the
                      buyer was not looking at. */}
                  <InquiryWorkflowActions
                    inquiry={inquiry}
                    onChanged={onChanged}
                    disabled={editing || !detail}
                    disabledReason={
                      editing
                        ? "Save or cancel the edit first."
                        : "Loading the inquiry…"
                    }
                    onOrder={startConvert}
                    onConfirmOrder={startConfirmOrder}
                  />
                {/* The email, opened on demand - the purchase order from
                    Ordered on, the RFQ before it. Never inline. */}
                {!editing && <EmailPreviewButton inquiry={inquiry} />}
              </>
            ))}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              {(inquiry.requisitions?.length ?? 0) > 1 ? "Requisitions" : "Requisition"}
            </Typography>
            <RequisitionLink inquiry={inquiry} onNavigate={onNavigate} />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              History
            </Typography>
          </Box>
          <RailScroll>
            {/* Detail responses only - the list omits auditTrail, so an
                inquiry that arrived from a list query renders the empty state
                rather than a wrong one. */}
            <InquiryAuditTrail
              auditTrail={
                historyAll || !inDelivery
                  ? (inquiry.auditTrail ?? [])
                  : (inquiry.auditTrail ?? []).filter(
                      (entry) => entry.action === "DELIVERY_UPDATE"
                    )
              }
            />
            {inDelivery && (inquiry.auditTrail ?? []).length > 1 && (
              <Button
                size="small"
                onClick={() => setHistoryAll((v) => !v)}
                sx={{ mt: 0.5 }}
              >
                {historyAll ? "Only goods in" : "Show all activity"}
              </Button>
            )}
          </RailScroll>
        </>
      }
    />
  );
};

// The inquiry facts, folded into a disclosure that opens on request. Empty
// fields are dropped, not drawn as "-" - a grid half-full of dashes reads as
// unfinished rather than as the three facts the buyer actually set.
// The Pickup / Delivery headline: how much of the order has arrived, as a
// stacked bar and a one-line rollup, plus the method. The board column shows
// the same numbers; the dialog leads with them because that is the state's
// only question - what came in.
const DeliveryHeadline: FunctionComponent<{
  progress: DeliveryProgressType | null;
  method: string;
  pickup: boolean;
}> = ({ progress, method, pickup }) => {
  const total = progress?.total ?? 0;
  const received = progress?.received ?? 0;
  const ready = progress?.ready ?? 0;
  const readyToUse = progress?.readyToUse ?? 0;
  const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          mb: 0.5,
        }}
      >
        <Typography variant="body2" fontWeight="bold">
          {/* In the order a piece travels, each against the whole order -
              the same three as the item-line columns. A delivery has no
              ready-to-pick-up stage, so it starts at what arrived. */}
          {total > 0
            ? [
                ...(pickup ? [`${ready} / ${total} ready to pick up`] : []),
                `${received} / ${total} ${receivedLabel(pickup).toLowerCase()}`,
                `${readyToUse} / ${total} ready to use`,
              ].join(" · ")
            : "No goods recorded yet"}
        </Typography>
        {method && (
          <Chip
            size="small"
            label={method}
            sx={{ height: 20, fontSize: "0.72rem" }}
          />
        )}
      </Box>
      {total > 0 && (
        <Box sx={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden" }}>
          {/* The counts are nested, so each segment is what that stage has
              that the one ahead of it does not - otherwise a fully collected
              line would draw two full bars' worth. */}
          <Box
            sx={{ width: `${pct(readyToUse)}%`, bgcolor: GOODS_IN_COLORS.readyToUse }}
          />
          <Box
            sx={{
              width: `${pct(Math.max(0, received - readyToUse))}%`,
              bgcolor: GOODS_IN_COLORS.received,
            }}
          />
          <Box
            sx={{
              width: `${pct(Math.max(0, ready - received))}%`,
              bgcolor: GOODS_IN_COLORS.readyToPickUp,
            }}
          />
          <Box sx={{ flexGrow: 1, bgcolor: "action.disabled" }} />
        </Box>
      )}
    </Box>
  );
};

//
// The route is /requisitions/<id>. It was written as a plain <a> to
// /outsourcing/requisitions?open=<id>, which is not a route: it matched
// /outsourcing/:id with id="requisitions", so it reloaded the whole app onto
// the Inquiries board and, because that board then persisted :id as its
// search term (useStateWithSessionStorage "inquirySearchVal"), left the board
// filtered to the string "requisitions" on every later visit.
const RequisitionLink: FunctionComponent<{
  inquiry: InquiryType;
  onNavigate: () => void;
}> = ({ inquiry, onNavigate }) => {
  // EE-253 P2b: an Inquiry composed from the pool can carry lines of several
  // requisitions; every one is listed. One whose lines all left the Inquiry
  // is struck through.
  if (inquiry.requisitions?.length) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }} data-testid="inquiry-requisitions">
        {inquiry.requisitions.map((req) => (
          <Typography
            key={req.id}
            variant="body2"
            sx={req.live ? undefined : { textDecoration: "line-through", opacity: 0.6 }}
          >
            <RouterLink to={`/requisitions/${req.id}`} onClick={onNavigate}>
              {req.requisitionNo}
            </RouterLink>
            {req.status ? ` · ${requisitionStatusLabel(req.status)}` : ""}
          </Typography>
        ))}
      </Box>
    );
  }
  if (!inquiry.requisitionId) {
    return (
      <Typography variant="body2" color="text.secondary">
        Raised directly — no requisition behind it.
      </Typography>
    );
  }
  return (
    <Typography variant="body2">
      <RouterLink
        to={`/requisitions/${inquiry.requisitionId}`}
        onClick={onNavigate}
      >
        REQ-{String(inquiry.requisitionId).padStart(6, "0")}
      </RouterLink>
    </Typography>
  );
};

// The saved inquiry's email, as the supplier receives it: Danish above,
// English below. Fetched by id rather than rebuilt from a form, because this
// view has no form - what matters here is what was actually sent. `enabled`
// off means nothing is fetched yet: the actions-column button only wants the
// email once it is pressed.
const useInquiryEmail = (inquiry: InquiryType, enabled: boolean) => {
  const { token } = useContext(AppStateContext);
  const [html, setHtml] = useState<string>("");
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  // The partner's id rather than the partner: the dialog re-reads the inquiry
  // after every workflow action, so the object is a new one each time and an
  // effect keyed on it refetched a preview that had not changed - which blanked
  // the iframe to a Skeleton and back on every status move.
  const partnerId = inquiry.partner?.id ?? inquiry.partnerId ?? undefined;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    // No setState("loading") here: a refetch keeps the email that is already
    // on screen until the new one arrives. The Skeleton is for the first load,
    // which is the only time there is nothing to show.
    apiPreviewInquiryFromId(token, inquiry.id, "html")
      .then((res) => {
        // The render endpoint answers text/plain on failure, so a 200 check is
        // the only thing separating an email from an error message rendered as
        // if it were one.
        if (!res.ok) throw new Error(String(res.status));
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        setHtml(text);
        setState("ready");
      })
      .catch(() => !cancelled && setState("error"));
    return () => {
      cancelled = true;
    };
    // EE-253: no longer gated on a supplier - an Inquiry raised from a
    // Requisition has none until one is picked off the shortlist, so the gate
    // had become permanent and the preview never appeared. The partner is
    // still a dependency: picking one re-renders the email under their name,
    // in place of the placeholder the backend renders without one.
  }, [token, inquiry.id, partnerId, enabled]);

  const downloadPDF = () => {
    apiPreviewInquiryFromId(token, inquiry.id, "pdf")
      .then((res) => res.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Inquiry-${inquiry.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      })
      .catch(console.error);
  };

  return { html, state, downloadPDF };
};

const PREVIEW_ERROR =
  "The preview could not be generated. Everything else on this inquiry is unaffected.";

const EmailPreviewButton: FunctionComponent<{ inquiry: InquiryType }> = ({
  inquiry,
}) => {
  const [open, setOpen] = useState(false);
  const [wanted, setWanted] = useState(false);
  const { html, state, downloadPDF } = useInquiryEmail(inquiry, wanted);

  return (
    <>
      <Button
        size="small"
        startIcon={<OpenInFull fontSize="small" />}
        onClick={() => {
          setWanted(true);
          setOpen(true);
        }}
        sx={{ mt: 1 }}
      >
        Email preview
      </Button>
      <EmailFullSizeDialog
        open={open}
        onClose={() => setOpen(false)}
        html={html}
        title={`Email preview for inquiry ${inquiry.id}`}
        actions={
          state === "ready" && (
            <Tooltip title="Download as PDF">
              <IconButton size="small" onClick={downloadPDF}>
                <Download fontSize="small" />
              </IconButton>
            </Tooltip>
          )
        }
      >
        {state === "loading" ? (
          <Skeleton variant="rectangular" width="100%" height={400} />
        ) : state === "error" ? (
          <Typography variant="body2" color="error">
            {PREVIEW_ERROR}
          </Typography>
        ) : undefined}
      </EmailFullSizeDialog>
    </>
  );
};

export default InquiryDialog;
